import { useNavigate } from 'react-router-dom';
import { Chip, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button, useDisclosure, Tooltip } from '@heroui/react';
import { motion } from 'framer-motion';
import { FaArrowRight, FaPlay, FaPause, FaEllipsisVertical, FaPenToSquare, FaCopy, FaTrash, FaBolt, FaLayerGroup, FaFilm, FaEye } from 'react-icons/fa6';
import ChannelAvatar from './primitives/ChannelAvatar.jsx';
import { Pill } from './primitives/bits.jsx';
import { channel as findChannel, lang as findLang, STATUS } from '../../services/autoDub/mockData.js';
import { fmtK } from '../../services/autoDub/cost.js';
import DeleteWorkflowModal from './DeleteWorkflowModal.jsx';

const STAT_ITEMS = [
	{ k: 'queue', label: 'In queue', color: '#9aa2b4' },
	{ k: 'dubbing', label: 'Dubbing', color: '#5A0AE0' },
	{ k: 'review', label: 'In review', color: '#f6a609' },
	{ k: 'published', label: 'Published', color: '#16c098' },
	{ k: 'failed', label: 'Failed', color: '#f23e5c' },
];

/* One workflow row on the Auto-Dub landing: animated source→dest flow, name + status,
   language / publish-mode / review pills, a stacked pipeline bar with legend and the
   monthly credit meter. */
export default function WorkflowCard({ wf, onToggle, index = 0 }) {
	const navigate = useNavigate();
	const deleteModal = useDisclosure();
	const src = findChannel(wf.source);
	const st = STATUS[wf.status] || STATUS.draft;
	const active = wf.status === 'active';
	const dests = (wf.dests || []).map(findChannel).filter(Boolean);
	const pct = Math.min(100, Math.round((wf.creditsMonth / wf.capMonth) * 100));
	const over = pct > 85;

	return (
		<motion.div
			initial={{ opacity: 0, y: 14 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ delay: 0.04 * index, type: 'spring', stiffness: 260, damping: 26 }}
			whileHover={{ y: -3 }}
			className="group relative overflow-hidden bg-white rounded-3xl border border-slate-200/80 shadow-[0_10px_40px_-28px_rgba(15,23,42,0.35)] hover:shadow-[0_22px_48px_-26px_rgba(90,10,224,0.32)] transition-shadow"
		>
			{active && <span aria-hidden className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-primary to-fuchsia-500" />}

			{/* ── Top tier ── */}
			<div className="p-[22px] pb-4 flex gap-5 items-start cursor-pointer" onClick={() => navigate(`/auto-dub/${wf.id}`)}>
				{/* source → dest flow */}
				<div className={`flex items-center gap-2.5 flex-none pt-0.5 ${active ? '' : '[filter:grayscale(.6)] opacity-80'}`}>
					<div className="relative">
						<ChannelAvatar ch={src} size={48} />
						{active && (
							<span className="absolute -right-1 -top-1 flex h-3 w-3">
								<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
								<span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-white" />
							</span>
						)}
					</div>

					{/* animated connector */}
					<div className="relative flex items-center w-10">
						<span className="flex-1 border-t-2 border-dashed border-slate-300" />
						<FaArrowRight size={13} className="text-slate-400 -ml-1" />
						{active && (
							<motion.span
								className="absolute top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-primary shadow-[0_0_8px_rgba(90,10,224,0.6)]"
								animate={{ left: ['0%', '85%'], opacity: [0, 1, 0] }}
								transition={{ duration: 1.7, repeat: Infinity, ease: 'easeInOut' }}
							/>
						)}
					</div>

					<div className="flex">
						{dests.slice(0, 3).map((d, i) => (
							<div key={d.id} className="rounded-[14px]" style={{ marginLeft: i ? -12 : 0, border: '2.5px solid #fff', zIndex: 3 - i }}>
								<ChannelAvatar ch={d} size={44} />
							</div>
						))}
						{dests.length > 3 && (
							<div className="grid place-items-center text-[13px] font-bold text-slate-500 bg-slate-200 rounded-[13px]" style={{ marginLeft: -12, width: 44, height: 44, border: '2.5px solid #fff' }}>+{dests.length - 3}</div>
						)}
					</div>
				</div>

				{/* body */}
				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-2.5 flex-wrap mb-2">
						<h3 className="m-0 text-[16.5px] font-semibold text-slate-800 truncate max-w-full">{wf.name}</h3>
						<Chip size="sm" variant="flat" color={st.tone} className="font-semibold"
							startContent={active
								? <span className="relative flex h-2 w-2 ml-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" /></span>
								: <span className="ml-1.5 inline-block h-2 w-2 rounded-full" style={{ background: st.color }} />}>
							{st.label}
						</Chip>
						<Chip size="sm" variant="flat" color={wf.type === 'watch' ? 'primary' : 'secondary'}
							startContent={<span className="ml-1">{wf.type === 'watch' ? <FaBolt size={11} /> : <FaLayerGroup size={11} />}</span>}
							className="font-semibold">
							{wf.type === 'watch' ? 'Watching new uploads' : 'Backfilling archive'}
						</Chip>
					</div>
					<div className="flex gap-1.5 flex-wrap">
						{wf.langs.map(code => {
							const l = findLang(code);
							return <Pill key={code}>{l.name}</Pill>;
						})}
						<Pill>{wf.publishMode === 'audio_track' ? <FaLayerGroup size={12} /> : <FaFilm size={12} />}{wf.publishMode === 'audio_track' ? 'Audio track' : 'New video'}</Pill>
						<Pill>{wf.review ? <FaEye size={12} /> : <FaBolt size={12} />}{wf.review ? 'Review before publish' : 'Auto-publish'}</Pill>
					</div>
				</div>

				{/* actions */}
				<div className="flex-none flex gap-2" onClick={e => e.stopPropagation()}>
					<Tooltip content={active ? 'Pause' : 'Resume'} placement="top">
						<Button isIconOnly size="md" radius="full" variant="flat"
							color={active ? 'default' : 'success'}
							onPress={() => onToggle(wf)} aria-label={active ? 'Pause' : 'Resume'}>
							{active ? <FaPause size={15} /> : <FaPlay size={15} />}
						</Button>
					</Tooltip>
					<Dropdown placement="bottom-end">
						<DropdownTrigger>
							<Button isIconOnly size="md" radius="full" variant="flat" aria-label="More"><FaEllipsisVertical size={16} /></Button>
						</DropdownTrigger>
						<DropdownMenu aria-label="Workflow actions">
							<DropdownItem key="edit" startContent={<FaPenToSquare size={14} />} onPress={() => navigate(`/auto-dub/${wf.id}/edit`)}>Edit workflow</DropdownItem>
							<DropdownItem key="duplicate" startContent={<FaCopy size={14} />} onPress={() => navigate(`/auto-dub/${wf.id}/duplicate`)}>Duplicate</DropdownItem>
							<DropdownItem key="delete" className="text-danger" color="danger" startContent={<FaTrash size={14} />} onPress={deleteModal.onOpen}>Delete</DropdownItem>
						</DropdownMenu>
					</Dropdown>
				</div>
			</div>

			{/* ── Footer strip: stat legend + compact credits ── */}
			<div className="px-[22px] pb-4">
				<div className="rounded-2xl border border-slate-100 bg-slate-50/60 px-3.5 py-2.5 flex flex-col lg:flex-row gap-x-5 gap-y-2 lg:items-center">
					{/* stat legend */}
					<div className="flex-1 min-w-0 flex gap-x-4 gap-y-1.5 flex-wrap">
						{STAT_ITEMS.map(s => (
							<div key={s.k} className="flex items-center gap-1.5">
								<span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
								<span className="text-[13px] font-bold text-slate-800 tabular-nums">{wf.stats[s.k]}</span>
								<span className="text-[12px] text-slate-500">{s.label}</span>
							</div>
						))}
					</div>

					{/* compact credits meter */}
					<div className="flex-none lg:w-[280px] flex items-center gap-2.5" onClick={e => e.stopPropagation()}>
						<span className="text-[11.5px] font-semibold text-slate-500 whitespace-nowrap">Credits</span>
						<div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden min-w-[60px]">
							<motion.div className="h-full rounded-full"
								initial={{ width: 0 }}
								animate={{ width: pct + '%' }}
								transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
								style={{ background: over ? '#f23e5c' : 'linear-gradient(90deg,#5A0AE0,#c026d3)' }} />
						</div>
						<span className="text-[11.5px] font-bold whitespace-nowrap tabular-nums" style={{ color: over ? '#f23e5c' : '#7b8499' }}>{pct}%</span>
						<span className="text-[13px] font-semibold text-slate-700 whitespace-nowrap tabular-nums">{fmtK(wf.creditsMonth)}/{fmtK(wf.capMonth)}</span>
					</div>
				</div>
			</div>

			<DeleteWorkflowModal wf={wf} isOpen={deleteModal.isOpen} onClose={deleteModal.onClose} />
		</motion.div>
	);
}
